import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { subscription, schools } from '@/lib/api';
import { ArrowLeft, Loader2, Check, CreditCard, AlertTriangle } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';
import AppFooter from '@/components/AppFooter';
import SubscriptionStatusCard from '@/components/subscription/SubscriptionStatusCard';
import ConfirmSubscriptionAction from '@/components/subscription/ConfirmSubscriptionAction';

const FEATURE_ORDER = [
  'multi_day_postings',
  'posting_completion_log',
  'full_analytics',
  'in_app_messaging',
  'position_reminders',
];

const FEATURE_LABELS = {
  multi_day_postings: 'Multi-day postings',
  posting_completion_log: 'Posting completion log',
  full_analytics: 'Full analytics & reporting',
  in_app_messaging: 'In-app sub messaging',
  position_reminders: 'Position reminders',
};

export default function Subscription() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [school, setSchool] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('head_start');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downgradeError, setDowngradeError] = useState(null);
  // Pending plan change awaiting confirmation: { type: 'upgrade'|'downgrade', plan }
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, statusRes, schoolRes] = await Promise.all([
          subscription.getPlans(schoolId),
          subscription.getStatus(schoolId),
          schools.get(schoolId)
        ]);
        setPlans(plansRes.data);
        setStatus(statusRes.data);
        setSchool(schoolRes.data);

        if (statusRes.data.plan) {
          setSelectedPlan(statusRes.data.plan);
        }
        if (statusRes.data.billing_cycle) {
          setBillingCycle(statusRes.data.billing_cycle);
        }
      } catch (error) {
        toast.error('Failed to load subscription data');
        navigate('/employer');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [schoolId, navigate]);

  const refreshStatus = async () => {
    const res = await subscription.getStatus(schoolId);
    setStatus(res.data);
    return res.data;
  };

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');

    if (sessionId && paymentStatus === 'success') {
      const checkPayment = async () => {
        try {
          const res = await subscription.checkoutStatus(sessionId);
          if (res.data.payment_status === 'paid') {
            toast.success('Payment successful! Your subscription is now active.');
            await refreshStatus();
          }
        } catch (_e) {
          // Payment status check failed - user can verify in subscription settings
        }
      };

      checkPayment();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, schoolId]);

  const currentPlanId = status?.plan || 'head_start';
  const isPastDue = status?.status === 'past_due';
  const isPaidActive = status?.status === 'active' && currentPlanId !== 'head_start';
  const isCancelling = isPaidActive && !!status?.cancel_at_period_end;
  const currentPlan = plans.find((p) => p.plan_id === currentPlanId);
  const pendingPlan = plans.find((p) => p.plan_id === status?.pending_plan_id);
  const selectedPlanData = plans.find((p) => p.plan_id === selectedPlan);
  const currentPlanIndex = plans.findIndex((p) => p.plan_id === currentPlanId);
  const selectedPlanIndex = plans.findIndex((p) => p.plan_id === selectedPlan);
  const showPlanPicker = !isPastDue && !isCancelling;

  const handleSubscribe = async () => {
    setIsSubmitting(true);
    setDowngradeError(null);

    try {
      const originUrl = window.location.origin;
      const res = await subscription.createCheckout(selectedPlan, billingCycle, originUrl, window.location.pathname);

      if (res.data.activated) {
        toast.success('Head Start plan activated');
        await refreshStatus();
        setIsSubmitting(false);
        return;
      }

      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setIsSubmitting(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setIsSubmitting(true);
    setDowngradeError(null);
    try {
      await subscription.upgrade(planId);
      toast.success('Plan upgraded');
      await refreshStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upgrade plan');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDowngrade = async (planId) => {
    setIsSubmitting(true);
    setDowngradeError(null);
    try {
      const res = await subscription.downgrade(planId);
      toast.success(
        res.data.billing_account?.pending_plan_id
          ? 'Downgrade scheduled for the end of your current billing period'
          : 'Plan downgraded'
      );
      await refreshStatus();
    } catch (error) {
      if (error.response?.status === 409 && error.response.data?.detail?.over_limits) {
        setDowngradeError(error.response.data.detail);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to downgrade plan');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSwitchBillingCycle = async (cycle) => {
    setIsSubmitting(true);
    try {
      const res = await subscription.switchBillingCycle(cycle);
      toast.success(
        res.data.billing_account?.pending_billing_cycle
          ? `Billing cycle change to ${cycle} scheduled for the end of your current billing period`
          : `Switched to ${cycle} billing`
      );
      await refreshStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch billing cycle');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsSubmitting(true);
    try {
      await subscription.cancel();
      toast.success('Subscription will be cancelled at the end of the billing period');
      await refreshStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManagePayment = async () => {
    setIsSubmitting(true);
    try {
      const res = await subscription.openBillingPortal(window.location.origin, window.location.pathname);
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
      setIsSubmitting(false);
    }
  };

  const getPrice = (plan) => {
    if (!plan) return 0;
    if (billingCycle === 'annual') {
      return plan.annual_price;
    }
    return plan.monthly_price;
  };

  const getSavings = (plan) => {
    if (!plan.monthly_price) return 0;
    const monthlyTotal = plan.monthly_price * 12;
    const annualPrice = plan.annual_price;
    return Math.round(((monthlyTotal - annualPrice) / monthlyTotal) * 100);
  };

  let primaryAction = null;
  if (selectedPlanData) {
    if (isPaidActive) {
      if (selectedPlan === currentPlanId) {
        primaryAction = { label: 'Current Plan', disabled: true };
      } else if (selectedPlan === 'head_start') {
        primaryAction = { label: 'Use "Cancel subscription" above to move to Head Start', disabled: true };
      } else if (selectedPlanIndex > currentPlanIndex) {
        // Money-related action: open the confirmation dialog explaining the
        // prorated payment before calling the API.
        primaryAction = {
          label: `Upgrade to ${selectedPlanData.name}`,
          onClick: () => setConfirmAction({ type: 'upgrade', plan: selectedPlanData }),
        };
      } else {
        primaryAction = {
          label: `Downgrade to ${selectedPlanData.name}`,
          onClick: () => setConfirmAction({ type: 'downgrade', plan: selectedPlanData }),
        };
      }
    } else if (selectedPlan === currentPlanId && status?.status !== 'cancelled') {
      primaryAction = { label: 'Current Plan', disabled: true };
    } else {
      primaryAction = {
        label: `Subscribe - $${getPrice(selectedPlanData)}${billingCycle === 'annual' ? '/year' : '/month'}`,
        onClick: handleSubscribe,
      };
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/employer">
            <Button variant="ghost" size="icon" className="text-white hover:bg-slate-800">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <BrandLogo variant="white" size="lg" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">
            Subscription
          </h1>
          <p className="text-slate-600 mt-2">
            {school?.name}
          </p>
        </div>

        {status && (
          <SubscriptionStatusCard
            status={status}
            planName={currentPlan?.name}
            isPaidActive={isPaidActive}
            isCancelling={isCancelling}
            isBusy={isSubmitting}
            pendingPlanName={pendingPlan?.name}
            pendingBillingCycle={status?.pending_billing_cycle}
            pendingChangeEffectiveDate={status?.pending_change_effective_date}
            onSwitchBillingCycle={handleSwitchBillingCycle}
            onCancelSubscription={handleCancelSubscription}
            onManagePayment={handleManagePayment}
          />
        )}

        {downgradeError?.over_limits && (
          <div className="mb-8 p-4 bg-rose-50 border border-rose-200 rounded-lg" data-testid="downgrade-over-limits-error">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-rose-700">
                  {downgradeError.message || "Current usage exceeds the target plan's limits"}
                </p>
                <ul className="mt-2 space-y-1 text-sm text-rose-600">
                  {Object.entries(downgradeError.over_limits).map(([resource, { current, limit }]) => (
                    <li key={resource}>
                      {resource.replace(/_/g, ' ')}: currently {current}, target plan allows {limit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {showPlanPicker && (
          <>
            {!isPaidActive && (
              <div className="flex justify-center mb-8">
                <div className="bg-slate-100 rounded-lg p-1 inline-flex">
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      billingCycle === 'monthly'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid="monthly-toggle"
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingCycle('annual')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      billingCycle === 'annual'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid="annual-toggle"
                  >
                    Annual
                    <Badge className="ml-2 bg-emerald-100 text-emerald-700">Save 20%</Badge>
                  </button>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {plans.map((plan) => (
                <Card
                  key={plan.plan_id}
                  className={`border-2 transition-all cursor-pointer ${
                    selectedPlan === plan.plan_id
                      ? 'border-slate-900 shadow-lg'
                      : 'border-slate-200 hover:border-slate-300'
                  } ${plan.plan_id === currentPlanId ? 'relative' : ''}`}
                  onClick={() => {
                    setSelectedPlan(plan.plan_id);
                    setDowngradeError(null);
                  }}
                  data-testid={`plan-${plan.plan_id}`}
                >
                  {plan.plan_id === currentPlanId && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-slate-900 text-white">Current Plan</Badge>
                    </div>
                  )}
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-xl font-['Manrope']">{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="text-center">
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-slate-900">
                        ${billingCycle === 'annual' ? Math.round(plan.annual_price / 12) : plan.monthly_price}
                      </span>
                      <span className="text-slate-500">/month</span>
                      {billingCycle === 'annual' && plan.monthly_price > 0 && (
                        <p className="text-sm text-emerald-600 mt-1">
                          ${plan.annual_price}/year (save {getSavings(plan)}%)
                        </p>
                      )}
                    </div>

                    <ul className="space-y-3 text-left mb-6">
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Up to {plan.max_subs === 9999 ? 'Unlimited' : plan.max_subs} Subs
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="h-4 w-4 text-emerald-600" />
                        {plan.max_postings_per_month >= 999999 ? 'Unlimited' : plan.max_postings_per_month} postings/month
                      </li>
                      {FEATURE_ORDER.map((featureKey) => {
                        const hasFeature = plan.features?.includes(featureKey);
                        return (
                          <li
                            key={featureKey}
                            className={`flex items-center gap-2 text-sm ${hasFeature ? 'text-slate-600' : 'text-slate-400 line-through'}`}
                          >
                            <Check className={`h-4 w-4 ${hasFeature ? 'text-emerald-600' : 'text-slate-300'}`} />
                            {FEATURE_LABELS[featureKey]}
                          </li>
                        );
                      })}
                    </ul>

                    <div className={`h-6 w-6 mx-auto rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === plan.plan_id
                        ? 'border-slate-900 bg-slate-900'
                        : 'border-slate-300'
                    }`}>
                      {selectedPlan === plan.plan_id && (
                        <Check className="h-4 w-4 text-white" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {primaryAction && (
              <div className="text-center">
                <Button
                  size="lg"
                  onClick={primaryAction.onClick}
                  disabled={isSubmitting || primaryAction.disabled}
                  className="bg-slate-900 hover:bg-slate-800 px-12"
                  data-testid="subscription-primary-action-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : primaryAction.disabled ? (
                    primaryAction.label
                  ) : (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" />
                      {primaryAction.label}
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Upgrade / downgrade confirmation — explains in detail what happens,
          in particular whether a new payment is charged (upgrades bill the
          prorated difference immediately; downgrades are only scheduled for
          the end of the billing period with no new payment). Kept in sync
          with SubscriptionAccount.jsx. */}
      {confirmAction && (
        <ConfirmSubscriptionAction
          open
          onOpenChange={(nextOpen) => !nextOpen && setConfirmAction(null)}
          title={confirmAction.type === 'upgrade'
            ? `Upgrade to ${confirmAction.plan.name}?`
            : `Downgrade to ${confirmAction.plan.name}?`}
          summary={confirmAction.type === 'upgrade'
            ? 'Applies immediately, and a new payment is charged — Stripe bills the prorated difference for the rest of your current billing period.'
            : `No new payment now. The switch is scheduled for ${status?.expires
              ? format(parseISO(status.expires), 'MMMM d, yyyy')
              : 'the end of your current billing period'}.`}
          confirmLabel={confirmAction.type === 'upgrade'
            ? `Upgrade to ${confirmAction.plan.name}`
            : `Schedule downgrade to ${confirmAction.plan.name}`}
          confirmTestId={confirmAction.type === 'upgrade' ? 'confirm-upgrade-btn' : 'confirm-downgrade-btn'}
          onConfirm={async () => {
            const { type, plan } = confirmAction;
            if (type === 'upgrade') {
              await handleUpgrade(plan.plan_id);
            } else {
              await handleDowngrade(plan.plan_id);
            }
            setConfirmAction(null);
          }}
          isBusy={isSubmitting}
        >
          {confirmAction.type === 'upgrade' ? (
            <ul className="space-y-1.5 list-disc pl-5">
              <li>
                Your plan switches to {confirmAction.plan.name} (
                ${getPrice(confirmAction.plan)}{billingCycle === 'annual' ? '/year' : '/month'}) right away, with its higher limits.
              </li>
              <li>Unused time on your current plan is credited against the prorated charge.</li>
              <li>Your billing date stays the same; from the next invoice you pay the full {confirmAction.plan.name} price.</li>
            </ul>
          ) : (
            <ul className="space-y-1.5 list-disc pl-5">
              <li>
                You keep {currentPlan?.name || 'your current plan'} and its limits until{' '}
                {status?.expires ? format(parseISO(status.expires), 'MMMM d, yyyy') : 'the end of the current period'}.
              </li>
              <li>
                From that date you are billed the {confirmAction.plan.name} price (
                ${getPrice(confirmAction.plan)}{billingCycle === 'annual' ? '/year' : '/month'}).
              </li>
              <li>If your current usage exceeds the {confirmAction.plan.name} limits, the downgrade is rejected and you&apos;ll see what to reduce first.</li>
            </ul>
          )}
        </ConfirmSubscriptionAction>
      )}
      <AppFooter role="employer" />
    </div>
  );
}
